import { useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Textarea } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { MOTIVO_MINIMO_PARA_REABRIR } from '@/data/atendimentoEmCurso';
import { dados } from '@/data/porta';

export interface ReabrirAtendimentoModalProps {
    atendimentoId: string;
    paciente: string;
    onFechar: () => void;
    onReaberta: () => void;
}

/**
 * Reabrir a consulta encerrada — com motivo.
 *
 * O protótipo reabria com um clique e sem rastro. Aqui o motivo é obrigatório e
 * fica na auditoria, e só a consulta encerrada hoje volta: a de outro dia pede
 * adendo no prontuário, não reescrita.
 */
export function ReabrirAtendimentoModal({ atendimentoId, paciente, onFechar, onReaberta }: ReabrirAtendimentoModalProps) {
    const aviso = useAviso();
    const idMotivo = useId();
    const [motivo, setMotivo] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const suficiente = motivo.trim().length >= MOTIVO_MINIMO_PARA_REABRIR;

    const reabrir = async () => {
        if (!suficiente) return;
        setSalvando(true);
        setErro(null);
        try {
            await dados().reabrirAtendimento(atendimentoId, motivo.trim());
            aviso({ title: 'Atendimento reaberto', description: paciente });
            onReaberta();
            onFechar();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível reabrir a consulta.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open width={480} onClose={onFechar}
            title="Reabrir atendimento?"
            description="A consulta volta a ficar aberta. O tempo em que ficou encerrada não entra na duração."
            dirty={Boolean(motivo.trim())} onSave={() => { void reabrir(); }} saveLabel="Reabrir"
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void reabrir(); }} disabled={!suficiente || salvando}>
                        {salvando ? 'Reabrindo…' : 'Reabrir atendimento'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" title="Não foi possível reabrir" dismissible={false}>{erro}</Alert>}
                <FormField label="Motivo" htmlFor={idMotivo} required hint="Fica registrado na auditoria do prontuário">
                    <Textarea
                        id={idMotivo} rows={3} value={motivo} onChange={setMotivo}
                        placeholder="Ex.: faltou registrar a conduta da segunda aplicação"
                    />
                </FormField>
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                    Só a consulta encerrada hoje pode ser reaberta. A de outro dia pede um adendo no prontuário.
                </p>
            </div>
        </Modal>
    );
}
